/*
 * File: DeliveryAddressGrid.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 30 Dec 2021 9:51:15 am
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { useRollbar } from '@rollbar/react';
import { Col, Row } from 'antd';
import { BlockLoader } from 'components/atoms/BlockLoader';
import { DeliveryAddressCard } from 'components/atoms/DeliveryAddressCard';
import NoResults from 'components/atoms/noResults/noResults';
import { WimsicalError } from 'components/atoms/WimsicalError';
import { useFormikContext } from 'formik';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import { useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ReduxState } from 'redux/rootReducer';
import { useGetDeliveryAddressesQuery } from 'redux/services/useBusterTheHorseService';

/* ****************** interfaces ****************** */
const styles: InlineStylesModel = {
  loadingRow: {
    width: '100%',
    paddingTop: 10
  },

  infiniteScrollRow: {
    overflowY: 'auto',
    overflowX: 'hidden',
    width: '100%',
    height: 'calc(100vh - 404px)'
  },
  moreLoading: {
    marginTop: 10
  },
  noData: {
    marginTop: 50
  },
  gridRow: {
    width: '100%',
    marginTop: '150px'
  },
  selectedCard: {
    margin: 0,
    padding: 0
  }
};

export const DeliveryAddressGrid = (): JSX.Element => {
  const { values } = useFormikContext<SalesOrderPayloadModel>();
  const { selectedCompanyDataAreaId, filterCity, filterCountry, filterDescription, filterState, filterStreet, filterZipCode } = useSelector((state: ReduxState) => state.salesOrders);
  const { acuityContext } = useSelector((state: ReduxState) => state.app);
  const { pathname } = useLocation();
  const isPosted = pathname.includes('/posted');

  const [offset, _setOffset] = useState(0);
  const rollbar = useRollbar();

  /* ****************** API/Hooks ****************** */

  const {
    data: deliveryAddressResponse,
    isLoading,
    isFetching,
    isError,
    error
  } = useGetDeliveryAddressesQuery(
    {
      dataAreaId: selectedCompanyDataAreaId || '',
      customerId: values.customerId,
      orderByField: 'AddressDescription',
      cityContains: filterCity || undefined,
      countryContains: filterCountry || undefined,
      stateContains: filterState || undefined,
      streetContains: filterStreet || undefined,
      zipCodeContains: filterZipCode || undefined,
      descriptionContains: filterDescription || undefined,
      offset
    },
    { skip: !selectedCompanyDataAreaId && !values.customerId }
  );

  /* ****************** Functions ****************** */
  const handleScrollNext = (): void => {
    _setOffset((prevState) => prevState + 25);
  };

  /* ****************** Variables ****************** */
  const hasMore = !!deliveryAddressResponse && !values.deliveryAddressLocationId && deliveryAddressResponse.data.length < deliveryAddressResponse.totalCount;

  /* ****************** JSX ****************** */

  const deliveryAddressCardsJSX = deliveryAddressResponse?.data.map(
    (address, index) =>
      address.customerId === values.customerId && (
        <Col key={index} span={24} xs={24} sm={12} md={12} lg={12} xl={12} xxl={12}>
          <DeliveryAddressCard deliveryAddress={address} />
        </Col>
      )
  );

  const selectedDeliveryAddress = (
    <Col xs={24} sm={12} md={12} lg={12}>
      <DeliveryAddressCard
        deliveryAddress={{
          street: values.deliveryAddressStreet,
          street2: '',
          city: values.deliveryAddressCity,
          state: values.deliveryAddressState,
          county: values.deliveryAddressCounty || '',
          country: values.deliveryAddressCountry || '',
          zipCode: values.deliveryAddressZipCode,
          customerId: values.customerId,
          dataAreaId: values.dataAreaId,
          description: values.deliveryAddressDescription ?? '',
          locationId: values.deliveryAddressLocationId
        }}
      />
    </Col>
  );

  /* ****************** Render ****************** */

  if (isError) {
    const queryError = error as FetchBaseQueryError;

    rollbar.error(`${queryError.status} - Error fetching addresses`, queryError, { selectedCustomerId: acuityContext?.selectedCustomer.id });

    return <WimsicalError status={typeof queryError.status === 'number' ? queryError.status : 500} message={''} />;
  }

  if (isLoading && offset === 0) {
    return (
      <div style={styles.infiniteScrollRow}>
        <BlockLoader direction="loader loader--slideUp" />
      </div>
    );
  }

  if (values.customerId && !deliveryAddressResponse?.data.length) {
    return (
      <Row justify="center" align="middle">
        <NoResults errorMessage="No Addresses Found" />
      </Row>
    );
  }

  // values.deliveryAddressLocationId ? selectedDeliveryAddress :

  return (
    <InfiniteScroll
      style={{ width: '100%', marginTop: '8px' }}
      dataLength={deliveryAddressResponse?.data.length || 0}
      next={handleScrollNext}
      hasMore={hasMore}
      loader={
        <Row justify="center" style={{ marginTop: 50 }}>
          <BlockLoader direction="loader loader--slideUp" />
        </Row>
      }
      scrollableTarget="scrollableDiv">
      <Row gutter={[16, 16]}>{isPosted ? selectedDeliveryAddress : deliveryAddressCardsJSX}</Row>
    </InfiniteScroll>
  );
};
